import { H2 } from 'foundations-components/transitional/components/H2';
import { useContext, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Form } from 'foundations-components/transitional/components/Form';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { RegexSafetyLevels, isSafeRegexUserInput, isRegex } from '../../utils/regexUtils';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Text } from 'foundations-components/components/Text';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const sanitizeOptions = options => {
  // Remove duplicates and empty strings, also trim whitespace
  return [...new Set(options.map(s => s.trim()).filter(s => s.length > 0))];
};
const textToOptions = (text, delimiter) => {
  const useDelimiter = delimiter && delimiter.length > 0;
  if (!useDelimiter) {
    return sanitizeOptions(text.split('\n'));
  }
  const delimiterIsRegex = isRegex(delimiter);
  if (delimiterIsRegex) {
    try {
      if (!isSafeRegexUserInput(delimiter, RegexSafetyLevels.DEFAULT)) {
        throw new Error('ReDoS validation failed');
      }
      const regex = new RegExp(delimiter.substring(1, delimiter.length - 1));
      return sanitizeOptions(text.split(regex));
    } catch (e) {
      return sanitizeOptions(text.split('\n'));
    }
  }
  return sanitizeOptions(text.split(delimiter));
};
const MultiValueEditor = ({
  onCancel: onCancelHandler,
  onConfirm: onConfirmHandler,
  value
}) => {
  const [text, setText] = useState(value.join('\n') || '');
  const [options, setOptions] = useState(textToOptions(value.join('\n') || ''));
  const [delimiter, setDelimiter] = useState('');
  const {
    getTrackerInstance,
    props: {
      baseFilterFamily,
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  useEffect(() => {
    var _getTrackerInstance;
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('openedMultiValueEditor', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
  }, [getTrackerInstance, baseFilterFamily, useOnePanel]);
  useEffect(() => {
    if (delimiter) {
      var _getTrackerInstance2;
      (_getTrackerInstance2 = getTrackerInstance()) === null || _getTrackerInstance2 === void 0 || _getTrackerInstance2.track('usedDelimiterInMultiValueEditor', {
        primaryObjectTypeId: baseFilterFamily,
        value: delimiter,
        version: useOnePanel ? '2' : '1'
      });
    }
  }, [getTrackerInstance, baseFilterFamily, delimiter, useOnePanel]);
  const delimiterIsInvalid = delimiter.length > 0 && isRegex(delimiter) && !isSafeRegexUserInput(delimiter, RegexSafetyLevels.DEFAULT);
  const onCancel = () => {
    var _getTrackerInstance3;
    onCancelHandler();
    (_getTrackerInstance3 = getTrackerInstance()) === null || _getTrackerInstance3 === void 0 || _getTrackerInstance3.track('canceledMultiValueEditor', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
  };
  const onSubmit = evt => {
    var _getTrackerInstance4;
    evt.preventDefault();
    onConfirmHandler(options);
    (_getTrackerInstance4 = getTrackerInstance()) === null || _getTrackerInstance4 === void 0 || _getTrackerInstance4.track('confirmedMultiValueEditor', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
  };
  return /*#__PURE__*/_jsx(Modal, {
    children: /*#__PURE__*/_jsxs(Form, {
      onSubmit: onSubmit,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.header"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: () => onCancel()
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [/*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.textAreaLabel"
          }),
          children: /*#__PURE__*/_jsx(TextArea, {
            blockSize: "100px",
            value: text,
            onChange: e => {
              setText(e.target.value);
              setOptions(textToOptions(e.target.value, delimiter));
            }
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.delimiterLabel"
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            error: delimiterIsInvalid,
            onChange: e => {
              setDelimiter(e.target.value);
              setOptions(textToOptions(text, e.target.value));
            }
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "small",
          display: "inline-block",
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.delimiterHelp"
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.preview",
            options: {
              count: options.length
            }
          }),
          children: /*#__PURE__*/_jsxs(ScrollContainer, {
            maxHeight: 100,
            gradientColor: "fill-surface-default",
            size: 28,
            children: [options.map(option => /*#__PURE__*/_jsx(Tag
            // Default margin-left is 12px but it leads to rows looking tabbed
            , {
              marginInlineStart: "0 !important",
              marginInlineEnd: "300",
              use: "default",
              children: option
            }, option)), options.length === 0 && /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.previewPlaceholder"
            })]
          })
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "tertiary",
          type: "submit",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.confirmButton"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          onClick: onCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.cancelButton"
          })
        })]
      })]
    })
  });
};
export default MultiValueEditor;